import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GlobalProvider, useGlobal } from './context/GlobalContext';
import SmartSuggestions from './components/shared/SmartSuggestions';
import SiriSpinner from './components/shared/SiriSpinner';
import './App.css';

// Eager load critical pages
import Toolbox from './pages/Toolbox';

// Lazy load other pages for better performance
const CostTracker = lazy(() => import('./pages/CostTracker'));
const Milestones = lazy(() => import('./pages/Milestones'));
const Checklists = lazy(() => import('./pages/Checklists'));
const GovResources = lazy(() => import('./pages/GovResources'));
const Documents = lazy(() => import('./pages/Documents'));
const BuilderLicenceCheck = lazy(() => import('./pages/BuilderLicenceCheck'));
const BuildingInsuranceCheck = lazy(() => import('./pages/BuildingInsuranceCheck'));
const SolarOrientation = lazy(() => import('./pages/SolarOrientation'));
const DefectsScanner = lazy(() => import('./pages/DefectsScanner'));
const FindABuilder = lazy(() => import('./pages/FindABuilder'));
const FindABusiness = lazy(() => import('./pages/FindABusiness'));
const SnapAndStyle = lazy(() => import('./pages/SnapAndStyle'));
const BuildPilotIntelligence = lazy(() => import('./pages/BuildPilotIntelligence'));
const QuoteComparison = lazy(() => import('./pages/QuoteComparison'));
const FacadeGenerator = lazy(() => import('./pages/Home3DRender'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Placeholder = lazy(() => import('./pages/Placeholder'));
const SpinnerDemo = lazy(() => import('./pages/SpinnerDemo'));
const SmartSuggestionsDemo = lazy(() => import('./pages/SmartSuggestionsDemo'));

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fff8ef' }}>
    <SiriSpinner size="large" text="Loading..." />
  </div>
);

const AppContent = () => {
  const { currentUserId, hasActiveSubscription } = useGlobal();
  const location = useLocation();
  
  // Only show SmartSuggestions when using tools AND user has active subscription
  const showSmartSuggestions = currentUserId && location.pathname.startsWith('/tools/') && hasActiveSubscription;
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fff8ef' }}>
      <main>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Navigate to={`/toolbox${location.search}`} replace />} />
            <Route path="/toolbox" element={<Toolbox />} />
            <Route path="/tools/cost-tracker" element={<CostTracker />} />
            <Route path="/tools/milestones" element={<Milestones />} />
            <Route path="/tools/checklists" element={<Checklists />} />
            <Route path="/tools/government-resources" element={<GovResources />} />
            <Route path="/tools/documents" element={<Documents />} />
            <Route path="/tools/builder-licence-check" element={<BuilderLicenceCheck />} />
            <Route path="/tools/building-insurance-check" element={<BuildingInsuranceCheck />} />
            <Route path="/tools/solar-orientation" element={<SolarOrientation />} />
            <Route path="/tools/defects-scanner" element={<DefectsScanner />} />
            <Route path="/tools/find-a-builder" element={<FindABuilder />} />
            <Route path="/tools/find-a-business" element={<FindABusiness />} />
            <Route path="/tools/snap-and-style" element={<SnapAndStyle />} />
            <Route path="/tools/buildpilot-intelligence" element={<BuildPilotIntelligence />} />
            <Route path="/tools/quote-comparison" element={<QuoteComparison />} />
            <Route path="/tools/facade-generator" element={<FacadeGenerator />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/spinner-demo" element={<SpinnerDemo />} />
            <Route path="/smart-suggestions-demo" element={<SmartSuggestionsDemo />} />
            <Route path="/advice" element={<Placeholder title="Advice" />} />
            <Route path="/designs" element={<Placeholder title="Designs" />} />
            <Route path="/builders" element={<Placeholder title="Builders" />} />
          </Routes>
        </Suspense>
      </main>
      
      {/* Smart Suggestions - Separate layer */}
      {showSmartSuggestions && <SmartSuggestions userId={currentUserId} />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <GlobalProvider>
        <AppContent />
      </GlobalProvider>
    </BrowserRouter>
  );
}

export default App;